import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type FormEvent, type ReactNode } from "react";
import { federatedProviderOf } from "./auth";
import { useAuth } from "./AuthProvider";
import { cognitoApi, passkeyAssertion, passkeysPossible, STEP_UP_METADATA } from "./cognito-api";
import { userContextData } from "./context-data";
import { deviceVerifier } from "./device-verifier";
import { mockPasskeyAssertion } from "./mock-passkey";
import { readTokens, removeRememberedDevice, unrememberSessionDevice, type DeviceSlot, type RememberedDevice } from "./session-store";
import { deviceNameFromUserAgent, signInErrorWords, SignInMachine, type SignInDeps, type SignInErrorCode } from "./sign-in-machine";
import { Dialog, btnPrimary, btnSecondary, inputClass } from "../components/ui";
import { isMock } from "../config";

/**
 * Step-up, "Confirm it's you" (requirements 13.3; crmarr/templates auth-self-hosted-sign-in.md R1, R3, R6, R8). Not a
 * timer: before a security change, the person must have proved who they are in the last fifteen minutes. If the
 * session's auth_time is older, a dialog asks once - a passkey, or the password and then the authenticator code - and
 * swaps the fresh tokens into the session. The API checks the same claim, so this is the polite half, not the only gate.
 *
 * One ceremony (R6, ported from gairm/lms 596e644): the dialog runs the sign-in machine in its step-up branch, with the
 * same context data (R8) and words; the session's own device key goes with the password, so a remembered device proves
 * itself (DEVICE_SRP_AUTH) and Cognito issues no new device. The tab keeps its own refresh token (#608).
 *
 * A federated person (ADR-0014, R7) has no password or code here: their proof is a fresh round trip through their
 * provider, asked for with `prompt=login`, which brings them back with a new auth_time.
 */
export const STEP_UP_MAX_AGE_S = 15 * 60;

/** The person closed "Confirm it's you": the action did not happen. */
export class StepUpCanceled extends Error {
  constructor() {
    super("You closed Confirm it's you, so nothing was changed. Try again when you are ready.");
    this.name = "StepUpCanceled";
  }
}

/** The session ended (here or in another tab) while "Confirm it's you" was open: the action did not happen. */
export class StepUpSessionEnded extends Error {
  constructor() {
    super("You were signed out, so nothing was changed. Sign in again, then try again.");
    this.name = "StepUpSessionEnded";
  }
}

interface StepUpState {
  /** Resolves when the session is fresh enough; rejects if the person cancels or the session ends. */
  ensureFresh: () => Promise<void>;
  isFresh: () => boolean;
}

const StepUpContext = createContext<StepUpState | undefined>(undefined);

export function StepUpProvider({ children }: { children: ReactNode }) {
  const { session, me } = useAuth();
  const [open, setOpen] = useState(false);
  const pending = useRef<{ promise: Promise<void>; resolve: () => void; reject: (e: unknown) => void }>();

  const isFresh = useCallback(() => Boolean(session?.authTime && Date.now() / 1000 - session.authTime < STEP_UP_MAX_AGE_S), [session]);
  const ensureFresh = useCallback((): Promise<void> => {
    if (isFresh()) return Promise.resolve();
    if (pending.current) return pending.current.promise;
    let resolve: () => void = () => undefined;
    let reject: (e: unknown) => void = () => undefined;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    pending.current = { promise, resolve, reject };
    setOpen(true);
    return promise;
  }, [isFresh]);

  const done = useCallback((ok: boolean, err?: unknown) => {
    const p = pending.current;
    pending.current = undefined;
    setOpen(false);
    if (ok) p?.resolve();
    else p?.reject(err ?? new StepUpCanceled());
  }, []);

  // Signed out while the dialog is open (here, or another tab): the action is refused in words, never left hanging.
  useEffect(() => {
    if (session || !pending.current) return;
    const p = pending.current;
    pending.current = undefined;
    setOpen(false);
    p.reject(new StepUpSessionEnded());
  }, [session]);

  const value = useMemo(() => ({ ensureFresh, isFresh }), [ensureFresh, isFresh]);
  const provider = me?.identityProvider ?? federatedProviderOf(session?.idToken);
  return (
    <StepUpContext.Provider value={value}>
      {children}
      {open && session && provider && <FederatedStepUp provider={provider} onDone={done} />}
      {open && session && !provider && <StepUpDialog username={session.username ?? session.email ?? ""} email={session.email ?? session.username ?? ""} accessToken={session.accessToken} onDone={done} />}
    </StepUpContext.Provider>
  );
}

export function useStepUp(): StepUpState {
  const ctx = useContext(StepUpContext);
  if (!ctx) throw new Error("useStepUp outside StepUpProvider");
  return ctx;
}

function FederatedStepUp({ provider, onDone }: { provider: string; onDone: (ok: boolean, err?: unknown) => void }) {
  const { signIn } = useAuth();
  const [busy, setBusy] = useState(false);
  return (
    <Dialog title="Confirm it's you" onClose={() => onDone(false)}>
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        This needs a recent sign-in, and yours was more than 15 minutes ago. You sign in through <span className="font-medium">{provider}</span>, so we'll send you there to sign in again and bring you straight back here. Then try again.
      </p>
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          className={btnPrimary}
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void signIn(window.location.pathname + window.location.search, { identityProvider: provider, stepUp: true });
          }}
        >
          Continue to {provider}
        </button>
        <button type="button" className={btnSecondary} onClick={() => onDone(false)}>
          Cancel
        </button>
      </div>
    </Dialog>
  );
}

/** The session's own device (session-store.ts), as the machine reads a remembered one: its key goes with the password. */
function sessionDevice(): NonNullable<SignInDeps["rememberedDevices"]> {
  const slot = (): DeviceSlot | undefined => {
    if (!isMock) return readTokens()?.device;
    try {
      return JSON.parse(window.sessionStorage.getItem("mock.device") ?? "null") as DeviceSlot | undefined;
    } catch {
      return undefined;
    }
  };
  return {
    read: (): RememberedDevice | undefined => {
      const d = slot();
      return d?.confirmed && d.password ? { key: d.key, groupKey: d.groupKey, password: d.password } : undefined;
    },
    write: () => undefined,
    remove: () => {
      const d = slot();
      if (!d) return;
      removeRememberedDevice({ key: d.key });
      unrememberSessionDevice(d.key);
    },
  };
}

/** The errors that belong to the field on screen; any other is the dialog's alert. */
const FIELD_ERRORS = new Set<SignInErrorCode>(["password-missing", "wrong-password", "code-format", "wrong-code", "code-used"]);

function StepUpDialog({ username, email, accessToken, onDone }: { username: string; email: string; accessToken: string | undefined; onDone: (ok: boolean, err?: unknown) => void }) {
  const { config, replaceTokens } = useAuth();
  const api = useMemo(() => cognitoApi(config!), [config]);
  const [machine] = useState(
    () =>
      new SignInMachine({
        api,
        stepUp: { username },
        clientMetadata: STEP_UP_METADATA,
        passkeysPossible,
        passkeyAssertion: isMock ? mockPasskeyAssertion : passkeyAssertion,
        contextData: isMock ? async () => ({ EncodedData: "mock-context-data" }) : (u) => userContextData(config!, u),
        deviceVerifier: (groupKey, deviceKey) => deviceVerifier(groupKey, deviceKey),
        deviceName: () => deviceNameFromUserAgent(navigator.userAgent),
        rememberedDevices: sessionDevice(),
        complete: async ({ tokens }) => {
          if (!tokens.IdToken || !tokens.AccessToken) throw new Error("Cognito answered without tokens");
          await replaceTokens(tokens);
        },
      }),
  );
  const state = useSyncExternalStore(
    (cb) => machine.subscribe(cb),
    () => machine.state,
  );
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const root = useRef<HTMLDivElement>(null);
  // A passkey is offered only to someone who has one: Cognito answers a passkey-less WEB_AUTHN preference otherwise.
  const [hasPasskey, setHasPasskey] = useState(false);
  useEffect(() => {
    if (!passkeysPossible() || !accessToken) return;
    let live = true;
    api
      .listPasskeys(accessToken)
      .then((r) => live && setHasPasskey((r.Credentials ?? []).length > 0))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [api, accessToken]);

  useEffect(() => {
    if (state.step.name === "done") onDone(true);
  }, [state.step.name, onDone]);
  useEffect(() => () => machine.restart(), [machine]);
  useEffect(() => {
    if (state.error && FIELD_ERRORS.has(state.error)) root.current?.querySelector<HTMLInputElement>("input")?.focus();
  }, [state]);

  const step = state.step.name;
  const offerPasskey = passkeysPossible() && hasPasskey && state.factors.includes("WEB_AUTHN");
  const fieldError = state.error && FIELD_ERRORS.has(state.error) ? signInErrorWords(state.error, true) : undefined;
  const otherError = state.error && !FIELD_ERRORS.has(state.error) ? signInErrorWords(state.error, true) : undefined;
  const submit = (fn: () => void) => (e: FormEvent) => {
    e.preventDefault();
    fn();
  };
  const errorId = "step-up-field-error";

  return (
    <Dialog title="Confirm it's you" onClose={() => onDone(false)}>
      <div ref={root} data-testid="step-up" data-step={step} className="min-w-0">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          This needs a recent sign-in, and yours was more than 15 minutes ago. Confirm it's you once, then carry on. Nothing else changes. Confirming as <span className="break-all font-medium text-ink dark:text-white">{email}</span>.
        </p>
        {otherError && (
          <p role="alert" data-testid="step-up-error" className="mt-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100">
            {otherError}
          </p>
        )}
        {(step === "password" || step === "choose") && (
          <>
            {offerPasskey && (
              <>
                <button type="button" className={`${btnPrimary} mt-4 w-full`} disabled={state.busy} onClick={() => void machine.usePasskey()}>
                  Use your passkey
                </button>
                <p className="mt-4 text-sm text-neutral-600 dark:text-neutral-400">Or use your password, then the code from your authenticator app.</p>
              </>
            )}
            <form className="mt-4 space-y-3" onSubmit={submit(() => void machine.submitPassword(password))} noValidate>
              <label className="block text-sm">
                <span className="block font-medium">Password</span>
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  className={inputClass}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={fieldError ? true : undefined}
                  aria-describedby={fieldError ? errorId : undefined}
                />
              </label>
              {fieldError && (
                <p id={errorId} className="text-sm text-red-700 dark:text-red-300">
                  {fieldError}
                </p>
              )}
              <div className="flex gap-2">
                <button type="submit" className={offerPasskey ? btnSecondary : btnPrimary} disabled={state.busy}>
                  {state.busy ? "Checking…" : "Continue with password"}
                </button>
                <button type="button" className={btnSecondary} onClick={() => onDone(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}
        {step === "passkey" && (
          <div className="mt-4 space-y-3">
            <p role="status" className="text-sm">
              Waiting for your passkey. Follow your browser's prompt to confirm with your fingerprint, face, PIN or security key.
            </p>
            <button type="button" className={btnSecondary} onClick={() => machine.usePassword()}>
              Use your password instead
            </button>
          </div>
        )}
        {step === "code" && (
          <form className="mt-4 space-y-3" onSubmit={submit(() => void machine.submitCode(code))} noValidate>
            <label className="block text-sm">
              <span className="block font-medium">Code from your authenticator app</span>
              <span className="block text-xs text-neutral-500">The six digits your authenticator app shows now.</span>
              <input
                inputMode="numeric"
                name="one-time-code"
                autoComplete="one-time-code"
                className={inputClass}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                aria-invalid={fieldError ? true : undefined}
                aria-describedby={fieldError ? errorId : undefined}
              />
            </label>
            {fieldError && (
              <p id={errorId} className="text-sm text-red-700 dark:text-red-300">
                {fieldError}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <button type="submit" className={btnPrimary} disabled={state.busy}>
                {state.busy ? "Checking…" : "Confirm code"}
              </button>
              <button
                type="button"
                className={btnSecondary}
                disabled={state.busy}
                onClick={() => {
                  setCode("");
                  setPassword("");
                  machine.restart();
                }}
              >
                Start again with your password
              </button>
            </div>
          </form>
        )}
        {(step === "finishing" || step === "done") && (
          <p role="status" className="mt-4 text-sm">
            Confirming…
          </p>
        )}
        <p className="mt-4 text-sm">
          <a href="/sign-in-help" target="_blank" rel="noopener noreferrer" className="text-accent-strong underline dark:text-accent">
            Trouble signing in? (opens in a new tab)
          </a>
        </p>
      </div>
    </Dialog>
  );
}
